import React, { useEffect, useMemo, useRef } from 'react';
import StatusTips from './StatusTips';
import { useRequestGpt } from '../hooks/useRequestGpt';
import useRequestFeedsState from '../hooks/useRequestFeedsState';
import {
  GPTErrorStatusType,
  GPTItemType,
  LoadingTipsType,
  PromptConfirmItemType,
} from '../types';
import { Col, Row } from 'tea-component';
import { SentimentType } from '@op/types/common';
import SvgIcon from '@home/components/SvgIcon';
import { sentimentIconMap } from '@op/constants';
import './GptCardView.scss';
import DOMPurify from 'dompurify';
import TextBubbles from '@home/components/TextBubbles';
import { COMMENTS_SELECTOR, HIDE_COMMENTS_CLASS } from './CommentsToggle';

export type LeftElementType = {
  gap?: number;
  span: number;
  element: any;
  textSpan?: number;
};

export function GptCardView({
  renderHtml,
  isEmpty,
  status,
  isMultiple,
  gptSumaryEndSlot,
  leftElement,
  GPTSentiment,
  summaryHtmlRef,
  hasBubble = false,
  showComments = true,
  onCommentsDetected,
  ...props
}: LoadingTipsType & {
  renderHtml: any;
  isEmpty: boolean;
  gptSumaryEndSlot?: React.ReactNode;
  leftElement?: LeftElementType;
  errorType?: GPTErrorStatusType;
  GPTSentiment?: SentimentType;
  summaryHtmlRef?: React.MutableRefObject<HTMLDivElement>;
  showRefreshBtn?: boolean;
  hasTotalText?: boolean;
  hasBubble?: boolean;
  /** 是否显示评论内容，默认 true */
  showComments?: boolean;
  /** 评论元素检测回调 */
  onCommentsDetected?: (hasComments: boolean) => void;
}) {
  const { element, gap, span, textSpan } = useMemo(() => {
    const { element, gap = 0, span = 0, textSpan } = leftElement || {};

    return {
      element,
      gap,
      span,
      textSpan: textSpan || 24 - span,
    };
  }, [leftElement]);

  // 内部 ref，用于 DOM 检测
  const internalSummaryHtmlRef = useRef<HTMLDivElement>(null);
  // 使用外部传入的 ref 或内部 ref
  const currentSummaryHtmlRef = summaryHtmlRef || internalSummaryHtmlRef;

  // DOM 检测：渲染后检测是否存在评论元素
  useEffect(() => {
    if (!isEmpty && renderHtml && currentSummaryHtmlRef.current) {
      const hasComments = !!currentSummaryHtmlRef.current.querySelector(COMMENTS_SELECTOR);
      onCommentsDetected?.(hasComments);
    }
  }, [renderHtml, isEmpty, onCommentsDetected]);

  if (isEmpty) {
    return (
      <StatusTips
        isMultiple={isMultiple}
        {...props}
        status={status}
      ></StatusTips>
    );
  }

  const showGptElement = (
    <span
      ref={currentSummaryHtmlRef as React.MutableRefObject<HTMLDivElement>}
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(renderHtml as string),
      }}
    ></span>
  );

  return (
    <Row
      className={`dbo-aigc-card-summary ${!showComments ? HIDE_COMMENTS_CLASS : ''}`.trim()}
      style={{ paddingTop: 0, paddingBottom: 0, marginTop: 0, marginBottom: 0 }}
      gap={gap}
    >
      <Col span={span} className="dbo-aigc-summary-left-info">
        {element}
      </Col>
      <Col span={textSpan} className="dbo-aigc-summary-text">
        {GPTSentiment && (
          <SvgIcon
            type={sentimentIconMap[GPTSentiment]}
            className="dbo-aigc-sentiment-icon"
          />
        )}
        {hasBubble ? (
          <TextBubbles rowNum={3} text={showGptElement} bubbleProps={{ overlayClassName: 'dbo-aigc-bubble' }}></TextBubbles>
        ) : (
          showGptElement
        )}
        {gptSumaryEndSlot && gptSumaryEndSlot}
      </Col>
    </Row>
  );
}

function MultipleItemGptCardView({
  hasStopBtn,
  handleStop,
  filter,
  changeAllGptMap,
  valueKey,
  promptInfo,
  dealwithParams,
  savePrefix,
  ...titleMap
}: {
  hasStopBtn: boolean;
  handleStop?: () => void;
  filter: any;
  changeAllGptMap: (key: string, value: GPTItemType) => void;
  valueKey: string;
  promptInfo?: PromptConfirmItemType;
  dealwithParams?: (data: any) => any;
  savePrefix: string;
  'zh-CN': string;
  'en-US': string;
}) {
  const params = useMemo(() => {
    return dealwithParams ? dealwithParams(filter) : filter;
  }, []);

  const { loading: stateLoading, total } = useRequestFeedsState({
    filter: params,
    needRequest: true,
  });

  const {
    status,
    percent,
    renderHtml,
    isEmpty,
    handleStop: localHandleStop,
    summary,
    requestGptData,
  } = useRequestGpt({
    filter: params,
    promptInfo,
    autoRequest: true,
    savePrefix,
  });

  useEffect(() => {
    changeAllGptMap(valueKey, {
      ...titleMap,
      summary,
      renderHtml,
      status,
    });
  }, [summary, renderHtml, status]);

  return (
    <GptCardView
      status={status}
      total={total}
      percent={percent}
      hasStopBtn={hasStopBtn}
      handleStop={() => localHandleStop()}
      isMultiple={true}
      renderHtml={renderHtml}
      isLittle={total < 10}
      isEmpty={isEmpty}
      promptInfo={promptInfo}
      reload={() => {
        requestGptData(promptInfo as PromptConfirmItemType);
      }}
    ></GptCardView>
  );
}

export type GPTListType = Array<{
  key: string;
  'zh-CN': string;
  'en-US': string;
  savePrefix: string;
  dealwithParams?: (filter: any) => any;
}>;
export function MultipleGptCardView({
  renderHtml,
  hasStopBtn,
  handleStop,
  changeAllGptMap,
  GPTList,
  filter,
  getTitle,
  showDefaultGpt,
  promptInfo,
  isEmpty,
}: {
  hasStopBtn: boolean;
  handleStop?: () => void;
  renderHtml: any;
  changeAllGptMap: (key: string, value: GPTItemType) => void;
  GPTList: GPTListType;
  filter: any;
  getTitle: (item: { 'zh-CN': string; 'en-US': string }) => string;
  showDefaultGpt?: boolean;
  promptInfo?: PromptConfirmItemType;
  isEmpty: boolean;
}) {
  if (showDefaultGpt && renderHtml) {
    return (
      <GptCardView
        renderHtml={renderHtml}
        status={'success'}
        isEmpty={isEmpty}
      ></GptCardView>
    );
  }

  return (
    <div className="dbo-aigc-card-summary-list">
      {GPTList.map(({ dealwithParams, key, savePrefix, ...titleProps }) => {
        return (
          <div className="dbo-aigc-card-summary-item" key={key}>
            <div className="dbo-aigc-card-summary-item-title">
              {getTitle(titleProps)}
            </div>
            <MultipleItemGptCardView
              hasStopBtn={hasStopBtn}
              handleStop={handleStop}
              key={key}
              valueKey={key}
              promptInfo={promptInfo}
              filter={filter}
              savePrefix={savePrefix}
              dealwithParams={dealwithParams}
              changeAllGptMap={changeAllGptMap}
              {...titleProps}
            ></MultipleItemGptCardView>
          </div>
        );
      })}
    </div>
  );
}
